/**
 * Testi italiani di Google come strumento (ADR 0011): collegamento con il file
 * JSON, guida per ottenerlo, pagina di gestione con i progetti Google Cloud e
 * la loro panoramica, collegamento di un progetto institor a Google.
 *
 * I nomi delle voci della console Google Cloud («IAM e amministrazione»,
 * «Service account», «Chiavi») restano come li mostra la console in italiano:
 * sono le parole che l'utente trova sulla pagina.
 */

/** Testi di Google. */
export const google = {
  /** Cassetto «Collega Google». */
  connect: {
    /** Pulsante che apre il cassetto e titolo del cassetto. */
    open: 'Collega Google',
    /** Pulsante di invio. */
    submit: 'Collega',
    /** Etichetta del metodo. */
    method: 'Tipo di credenziale',
    /** Aiuto sotto il nome. */
    labelHelp: 'Ti serve a riconoscerla, per esempio «Google lavoro».',
    /** Etichetta dell'area del JSON. */
    json: 'File JSON della credenziale',
    /** Aiuto sotto l'area del JSON. */
    jsonHelp: 'Incolla il contenuto del file o caricalo. Il file resta nel browser: parte solo quando premi «Collega».',
    /** Pulsante che carica il file. */
    upload: 'Carica file',
    /** File caricato. */
    loaded: 'Letto {{name}}.',
    /** JSON mancante. */
    jsonMissing: 'Incolla il JSON o carica il file prima di collegare.',
    /** Testo che non è JSON. */
    jsonInvalid: 'Il testo non è un JSON valido. Copia il file per intero, dalla prima all’ultima parentesi graffa.',
    /** JSON dell'altro tipo. */
    typeMismatch: 'Questo file è di un service account: scegli «Service account» come tipo.',
    /** JSON dell'altro tipo, al contrario. */
    typeMismatchUser: 'Questo file contiene le credenziali di un account: scegli «Credenziali del tuo account» come tipo.',
    /** JSON troppo lungo. */
    tooLong: 'Il file supera 8192 caratteri. Controlla di aver scelto il file giusto.',
    /** File illeggibile. */
    readFailed: 'Lettura del file non riuscita. Riprova o incolla il contenuto.',
    /** Esito riuscito. */
    done: 'Google collegato.',
    /** Esito fallito. */
    failed: 'Collegamento non riuscito. Riprova.',
  },

  /** Guida per ottenere il JSON. */
  guide: {
    /** Credenziali dell'account. */
    user: {
      /** Primo passo. */
      install: 'Installa Google Cloud CLI, se non ce l’hai:',
      /** Testo del collegamento. */
      installLink: 'istruzioni di installazione',
      /** Secondo passo. */
      run: 'In un terminale esegui questo comando e accedi con l’account Google che gli agenti useranno:',
      /** Nota sugli scope. */
      scopes: 'Lo scope cloud-platform è quello che serve: senza, il collegamento viene rifiutato.',
      /** Terzo passo. */
      file: 'Solo questo comando scrive il file application_default_credentials.json: gcloud init e gcloud auth login non lo creano. Alla fine il terminale ne stampa il percorso; di solito è qui:',
      /** Ultimo passo. */
      paste: 'Carica quel file qui sotto, o aprilo e incollane il contenuto.',
      /** Avvertenza. */
      warning:
        'La credenziale agisce con tutti i permessi del tuo account su Google Cloud. Nei progetti la limiti a un progetto e alla sola lettura.',
    },
    /** Service account. */
    serviceAccount: {
      /** Primo passo. */
      open: 'Apri la pagina dei service account nella console Google Cloud:',
      /** Testo del collegamento. */
      openLink: 'service account',
      /** Secondo passo. */
      create: 'Scegli il progetto, poi premi «Crea service account» e dagli un nome, per esempio «institor».',
      /** Terzo passo. */
      roles: 'Assegna i ruoli che servono agli agenti, per esempio «Visualizzatore» per la sola lettura.',
      /** Quarto passo. */
      key: 'Apri il service account, scheda «Chiavi», poi «Aggiungi chiave» e «Crea nuova chiave» di tipo JSON.',
      /** Ultimo passo. */
      paste: 'Il browser scarica il file JSON: caricalo qui sotto.',
      /** Avvertenza. */
      warning: 'Il service account vede solo i progetti in cui ha un ruolo.',
    },
    /** Che cosa succede dopo. */
    after:
      'Dopo che premi «Collega», institor verifica la credenziale con Google, la cifra e non la mostra più. Gli agenti non la vedono mai.',
  },

  /** Errori di Google. */
  errors: {
    /** Credenziale rifiutata. */
    invalid: 'Google rifiuta la credenziale. Controlla di aver scelto il tipo giusto e di aver copiato il file per intero.',
    /** Scope mancante. */
    scopeMissing:
      'La credenziale non dà accesso a Google Cloud. Ripeti il comando gcloud con lo scope cloud-platform e carica il file nuovo.',
    /** Google non raggiungibile. */
    unreachable: 'Google non risponde. Riprova fra qualche minuto.',
    /** Connessione già presente. */
    connectionExists: 'Esiste già una connessione con questo nome per questo account Google. Scegli un altro nome.',
    /** Rotazione con un altro account. */
    accountMismatch:
      'La credenziale appartiene a un altro account Google, o è di un altro tipo. Per un altro account crea una connessione nuova.',
    /** Operazione non valida per il fornitore. */
    providerMismatch: 'Questa operazione non vale per una connessione Google.',
    /** Identificativo di progetto già preso. */
    projectExists: 'Questo ID di progetto è già usato su Google Cloud. Scegline un altro.',
    /** Creazione di progetto rifiutata. */
    projectRefused: 'Google rifiuta la creazione del progetto.',
  },

  /** Rotazione della credenziale. */
  edit: {
    /** Etichetta. */
    json: 'Nuovo file JSON',
    /** Aiuto. */
    jsonHelp: 'Lascia vuoto per tenere quello attuale. La credenziale attuale non si può rileggere.',
  },

  /** Pagina di gestione di una connessione Google. */
  manage: {
    /** Briciola della pagina degli strumenti. */
    crumb: 'Strumenti',
    /** Descrizione. */
    description: 'I progetti Google Cloud che questa credenziale vede e che cosa contengono.',
    /** Connessione non trovata. */
    notFound: 'Connessione Google non trovata.',
    /** Spiegazione della connessione non trovata. */
    notFoundText: 'Forse è stata eliminata, o non è tua. Torna agli strumenti.',
    /** Pulsante di ritorno. */
    back: 'Torna agli strumenti',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento della connessione non riuscito. Riprova.',
    /** Titolo dei dati della connessione. */
    details: 'Connessione',
    /** Account. */
    account: 'Account',
    /** Stato. */
    status: 'Stato',
    /** Metodo. */
    method: 'Tipo di credenziale',
    /** Scope. */
    scopes: 'Scope concessi',
    /** Ultima verifica. */
    checked: 'Verificata il',
    /** Progetti institor che la usano. */
    bindings: 'Progetti institor collegati',
    /** Numero di progetti. */
    bindingsCount_one: '{{count}} progetto',
    /** Numero di progetti. */
    bindingsCount_other: '{{count}} progetti',
    /** Nessuno scope. */
    noScopes: 'Nessuno',
  },

  /** Progetti Google Cloud. */
  projects: {
    /** Titolo della sezione. */
    title: 'Progetti Google Cloud',
    /** Casella di ricerca. */
    search: 'Cerca un progetto',
    /** Nessun progetto. */
    empty: 'La credenziale non vede nessun progetto Google Cloud.',
    /** Nessun risultato della ricerca. */
    noMatch: 'Nessun progetto corrisponde alla ricerca.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento dei progetti non riuscito. Riprova.',
    /** Colonne. */
    columns: {
      /** Nome. */
      name: 'Nome',
      /** Identificativo. */
      id: 'ID progetto',
      /** Numero. */
      number: 'Numero',
      /** Stato. */
      state: 'Stato',
    },
    /** Pulsante di apertura della panoramica. */
    open: 'Apri',
    /** Nome accessibile dell'apertura. */
    openNamed: 'Apri {{name}}',
    /** Stato attivo. */
    active: 'Attivo',
    /** Stato di un progetto appena creato che Google sta ancora preparando. */
    pending: 'In preparazione',
    /** Scelta del progetto nel collegamento. */
    select: 'Progetto Google Cloud',
    /** Tutti i progetti. */
    all: 'Tutti i progetti visibili',
    /** Testo di riempimento. */
    placeholder: 'Scegli un progetto',
  },

  /** Panoramica di un progetto. */
  overview: {
    /** Titolo, con il nome del progetto. */
    title: 'Panoramica di {{name}}',
    /** Nessun progetto scelto. */
    pick: 'Apri un progetto per vederne API, chiavi e accesso degli utenti.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento della panoramica non riuscito. Riprova.',
    /** Chiudi la panoramica. */
    close: 'Chiudi panoramica',
    /** Stati delle sezioni. */
    status: {
      /** API spenta. */
      disabled: 'L’API di Google che serve a leggere questa sezione è spenta nel progetto.',
      /** Permesso mancante. */
      forbidden: 'La credenziale non ha il permesso di leggere questa sezione.',
      /** Errore. */
      error: 'Google non ha risposto come previsto. Riprova più tardi.',
    },
    /** API attive. */
    services: {
      /** Titolo. */
      title: 'API attive',
      /** Nessuna API. */
      empty: 'Nessuna API attiva nel progetto.',
      /** Gruppi in evidenza. */
      groups: {
        /** Autenticazione. */
        auth: 'Autenticazione',
        /** Mappe. */
        maps: 'Mappe',
        /** Firebase. */
        firebase: 'Firebase',
        /** Altre. */
        other: 'Altre API',
      },
      /** Gruppo vuoto. */
      noneInGroup: 'Nessuna',
      /** Titolo di un gruppo con il numero di API. */
      groupTitle: '{{name}} ({{count}})',
    },
    /** Chiavi API. */
    apiKeys: {
      /** Titolo. */
      title: 'Chiavi API',
      /** Che cosa si mostra. */
      intro: 'Nome e restrizioni delle chiavi. Il valore delle chiavi non si legge da qui.',
      /** Nessuna chiave. */
      empty: 'Nessuna chiave API nel progetto.',
      /** Colonne. */
      columns: {
        /** Nome. */
        name: 'Nome',
        /** Creazione. */
        created: 'Creata il',
        /** Restrizioni. */
        restrictions: 'Restrizioni',
        /** API consentite. */
        targets: 'API consentite',
      },
      /** Nessuna restrizione. */
      unrestricted: 'Nessuna restrizione',
      /** Tutte le API. */
      allApis: 'Tutte',
    },
    /** Accesso degli utenti. */
    auth: {
      /** Titolo. */
      title: 'Accesso degli utenti',
      /** Metodi di accesso. */
      signIn: 'Metodi di accesso',
      /** Domini autorizzati. */
      domains: 'Domini autorizzati',
      /** Nessun metodo. */
      noSignIn: 'Nessun metodo di accesso abilitato.',
      /** Nessun dominio. */
      noDomains: 'Nessun dominio autorizzato.',
      /** Nomi dei metodi noti. */
      methods: {
        /** Email e password. */
        email: 'Email e password',
        /** Telefono. */
        phone: 'Telefono',
        /** Anonimo. */
        anonymous: 'Anonimo',
      },
    },
  },

  /** Creazione di un progetto Google Cloud. */
  newProject: {
    /** Pulsante che apre il cassetto e titolo del cassetto. */
    open: 'Nuovo progetto',
    /** Pulsante di invio. */
    submit: 'Crea progetto',
    /** Etichetta del nome. */
    name: 'Nome',
    /** Aiuto sotto il nome. */
    nameHelp: 'Facoltativo, da 4 a 30 caratteri. È il nome che vedi nella console Google Cloud.',
    /** Nome di lunghezza sbagliata. */
    nameLength: 'Il nome deve avere da 4 a 30 caratteri, oppure lascialo vuoto.',
    /** Etichetta dell'identificativo. */
    id: 'ID progetto',
    /** Aiuto sotto l'identificativo. */
    idHelp:
      'Da 6 a 30 caratteri fra minuscole, cifre e trattini, con una lettera all’inizio. È unico su tutto Google e non si cambia dopo.',
    /** Identificativo mancante. */
    idMissing: 'Scrivi l’ID del progetto.',
    /** Identificativo malformato. */
    idInvalid:
      'L’ID non va bene. Usa da 6 a 30 caratteri fra minuscole, cifre e trattini, con una lettera all’inizio.',
    /** Identificativo già preso. */
    exists: 'L’ID {{id}} è già usato su Google Cloud, anche da altri account. Scegline un altro.',
    /** Google rifiuta la creazione, con il motivo scritto da Google. */
    refused: 'Google non ha creato il progetto. Motivo indicato da Google: {{reason}}',
    /** Creazione riuscita. */
    done: 'Progetto {{id}} creato.',
    /** Creazione accettata ma non ancora conclusa. */
    pending: 'Google sta ancora preparando il progetto {{id}}: fra qualche minuto sarà attivo.',
    /** Creazione fallita. */
    failed: 'Creazione del progetto non riuscita. Riprova.',
    /** Opzione del selettore per un identificativo scritto. */
    createOption: 'Crea il progetto {{id}}',
    /** Opzione del selettore per un nome da cui si è ricavato l'identificativo. */
    createOptionNamed: 'Crea il progetto {{id}} con nome «{{name}}»',
    /** Etichetta accanto a un progetto da creare. */
    tag: 'Nuovo',
    /** Aiuto sotto il selettore con un progetto da creare. */
    willCreate: 'Il progetto non esiste ancora: viene creato su Google Cloud all’invio, poi collegato.',
    /** Aiuto quando dal testo scritto non si ricava un ID. */
    typeHint: 'Scrivi un nome o un ID per creare un progetto nuovo.',
  },

  /** Collegamento di un progetto institor a Google. */
  binding: {
    /** Che cosa succede. */
    intro:
      'Gli agenti del progetto useranno la credenziale solo sul progetto Google Cloud che scegli qui, con l’accesso indicato.',
    /** Aiuto sotto la scelta del progetto. */
    projectHelp: 'Con «Tutti i progetti visibili» gli agenti raggiungono ogni progetto che la credenziale vede.',
    /** Spiegazione della lettura. */
    readHelp: 'Lettura: gli agenti possono solo leggere, con richieste GET. Alcune ricerche di Google restano escluse.',
    /** Spiegazione della scrittura. */
    writeHelp: 'Lettura e scrittura: gli agenti possono anche creare, modificare e cancellare, per esempio attivare API o creare chiavi.',
    /** Tutti i progetti, nella tabella. */
    allProjects: 'Tutti i progetti Google Cloud',
    /** Riassunto delle regole in sola lettura. */
    rulesRead: 'Solo lettura',
    /** Riassunto delle regole in scrittura. */
    rulesWrite: 'Lettura e modifiche',
    /** Spiegazione nel cassetto di modifica. */
    editIntro: 'Connessione: {{connection}}.',
    /** Collegamento già presente: uno per connessione Google. */
    duplicate:
      'Questa connessione Google è già collegata al progetto. Modifica il collegamento esistente, o usa un’altra connessione per un secondo progetto Google Cloud.',
  },
};

/** Forma dei testi di Google. */
export type Google = typeof google;
